// Port of load_amex_csv in scripts/loaders.py.
// AmEx native convention: positive = charge (expense), negative = payment/refund.
// Matches our positive=expense convention — no sign flip needed.
// AUTOPAY rows filter out as inter-portal transfers via "payment ... thank you".

import {
  type Transaction,
  newTransaction,
  makeId,
  parseDate,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

export async function loadAmex(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  const headerIdx = findHeaderIndex(rows, ["Date", "Description"]);
  if (headerIdx < 0) return [];
  const header = rows[headerIdx].map((h) => (h || "").trim());
  const dictRows = toDictRows(header, rows.slice(headerIdx + 1));

  const out: Transaction[] = [];
  for (const row of dictRows) {
    const rawDate = (row["Date"] || row["Transaction Date"] || "").trim();
    if (!rawDate) continue;
    let dateStr: string;
    try {
      dateStr = parseDate(rawDate);
    } catch {
      continue;
    }
    const amtRaw =
      row["Amount"] || row["$Amount"] || row["Charge Amount"] || "0";
    const amount = parseAmount(amtRaw);
    if (amount === 0 && !amtRaw) continue;

    const description = (row["Description"] || row["Merchant"] || "")
      .trim()
      .replace(/^"|"$/g, "");
    if (isInterPortalTransfer(description)) continue;

    const accountLabel = "AmEx";
    const id = await makeId("amex", accountLabel, dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "amex",
        amount: Math.round(amount * 100) / 100,
        portal: "amex",
        account: accountLabel,
        account_kind: "liability", // AmEx exports are always credit-card style
        account_last4: extractLast4(row["Account #"] || row["Card Member"] || ""),
        source_file: filename,
        raw: { ...row },
      }),
    );
  }
  return out;
}
